import { type KeyboardEvent, forwardRef, useRef, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../lib/utils";
import { Button } from "./button";
import { Textarea } from "./textarea";
import { ArrowUp, Clock, Plus, Settings, Sparkles } from "lucide-react";

export interface AIChatInputProps {
  /**
   * Current input value
   */
  value: string;
  /**
   * Callback when input value changes
   */
  onChange: (value: string) => void;
  /**
   * Callback when send button is clicked or Enter is pressed
   */
  onSend: () => void;
  /**
   * Callback when history button is clicked
   */
  onHistoryClick?: () => void;
  /**
   * Placeholder text for the input
   */
  placeholder?: string;
  /**
   * Whether the input is disabled
   */
  disabled?: boolean;
  /**
   * Whether a message is currently being sent/loading
   */
  isLoading?: boolean;
  /**
   * Optional additional className for the container
   */
  className?: string;
}

export interface AIChatInputRef {
  focus: () => void;
}

/**
 * AIChatInput - Input component for AI chat interfaces
 *
 * Features:
 * - Textarea with auto-grow
 * - Enter to send (Shift+Enter for newline)
 * - Action buttons: attachment (+), settings, history (clock), sparkle, send
 * - Only history and send buttons are functional; others are visual placeholders
 *
 * @example
 * ```tsx
 * const [message, setMessage] = useState("");
 *
 * <AIChatInput
 *   value={message}
 *   onChange={setMessage}
 *   onSend={() => sendMessage(message)}
 *   onHistoryClick={() => setShowHistory(true)}
 *   placeholder="What can I help you with?"
 * />
 * ```
 */
export const AIChatInput = forwardRef<AIChatInputRef, AIChatInputProps>(
  ({ value, onChange, onSend, onHistoryClick, placeholder, disabled = false, isLoading = false, className }, ref) => {
    const { t } = useTranslation("ai");
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const resolvedPlaceholder = placeholder ?? t("What can I help you with?");

    useImperativeHandle(ref, () => ({
      focus: () => textareaRef.current?.focus(),
    }));

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (value.trim() && !disabled && !isLoading) {
          onSend();
        }
      }
    };

    const canSend = value.trim().length > 0 && !disabled && !isLoading;

    return (
      <div className={cn("border rounded-lg bg-background", className)}>
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={resolvedPlaceholder}
          disabled={disabled || isLoading}
          className="min-h-[60px] max-h-[200px] border-0 focus-visible:ring-0 resize-none"
        />

        <div className="flex items-center justify-between px-3 py-2 border-t">
          {/* Left side action buttons (visual placeholders) */}
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              disabled
              hoverLabel={t("Attach file (coming soon)")}
            >
              <Plus className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              disabled
              hoverLabel={t("Settings (coming soon)")}
            >
              <Settings className="h-4 w-4" />
            </Button>
            {onHistoryClick && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={onHistoryClick}
                hoverLabel={t("Chat history")}
              >
                <Clock className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/* Right side action buttons */}
          <div className="flex items-center gap-1">
            <Sparkles className="h-4 w-4 text-muted-foreground mr-2" />
            <Button
              type="button"
              variant={canSend ? "default" : "ghost"}
              size="icon"
              className="h-8 w-8"
              onClick={onSend}
              disabled={!canSend}
              loading={isLoading}
              hoverLabel={t("Send message")}
            >
              <ArrowUp className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }
);

AIChatInput.displayName = "AIChatInput";
